import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SearchBar from "../components/SearchBar";
import AllRestaurants from "../components/AllRestaurants";
import RestaurantFoodGroup from "../components/RestaurantFoodGroup";
import { IFoodCategory } from "../models/IFoodCategory";
import { getFoodCategoryList } from "../services/foodCategory";
import { IRestaurantFoodGroup, searchGroupedByRestaurant } from "../services/search";
import { fetchVerificationStatus } from "../utils/verificationCache";
import { isLoggedIn } from "../utils/tokenUtils";
import { useLanguage } from "../context/LanguageContext";
import "../styles/style.css";

function TumMekanlar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, lang } = useLanguage();

  const categoryId = searchParams.get("categoryId");
  // Arama çubuğunun butonu/Enter'ı buraya ?q= ile gelir.
  const searchTerm = searchParams.get("q")?.trim() ?? "";
  const hasFilter = !!categoryId || !!searchTerm;

  const [category, setCategory] = useState<IFoodCategory | null>(null);
  const [results, setResults] = useState<IRestaurantFoodGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isLoggedIn()) return;
    fetchVerificationStatus();
  }, []);

  const nameOf = useCallback(
    (c: IFoodCategory) => (lang === "en" ? c.foodCategoryNameEn : c.foodCategoryNameTr),
    [lang]
  );

  // Arama terimi ve kategori filtresi URL'de yaşıyor; böylece geri/ileri
  // tuşları ve paylaşılan bağlantı da çalışır. Kategori etiketinin adı ve
  // resmi için kategori listesi okunur.
  useEffect(() => {
    if (!categoryId && !searchTerm) {
      setCategory(null);
      setResults([]);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    const run = async () => {
      let foodCategoryId: string | undefined;
      if (categoryId) {
        const list = await getFoodCategoryList();
        const found = list.data.find((c) => c.id === categoryId) ?? null;
        if (cancelled) return;
        setCategory(found);
        if (!found) {
          setResults([]);
          return;
        }
        foodCategoryId = found.id;
      } else {
        setCategory(null);
      }

      const groups = await searchGroupedByRestaurant({ query: searchTerm, foodCategoryId });
      if (!cancelled) setResults(groups);
    };

    run()
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId, searchTerm]);

  const clearParam = (key: "categoryId" | "q") => {
    searchParams.delete(key);
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="container py-4">
      <SearchBar />

      {hasFilter && (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          {searchTerm && (
            <>
              <span className="text-muted small fw-semibold">{t("search.searchedFor")}:</span>
              <span className="category-filter-chip">
                <i className="bi bi-search" style={{ fontSize: "0.75rem" }}></i>
                {searchTerm}
                <button
                  type="button"
                  className="category-filter-chip__remove"
                  onClick={() => clearParam("q")}
                  aria-label={t("categories.clear")}
                  title={t("categories.clear")}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: "0.75rem" }}></i>
                </button>
              </span>
            </>
          )}
          {categoryId && (
            <>
              <span className="text-muted small fw-semibold">{t("categories.filteredBy")}:</span>
              <span className="category-filter-chip">
                {category?.foodCategoryImageUrl && (
                  <img className="category-filter-chip__img" src={category.foodCategoryImageUrl} alt="" />
                )}
                {category ? nameOf(category) : "…"}
                <button
                  type="button"
                  className="category-filter-chip__remove"
                  onClick={() => clearParam("categoryId")}
                  aria-label={t("categories.clear")}
                  title={t("categories.clear")}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: "0.75rem" }}></i>
                </button>
              </span>
            </>
          )}
        </div>
      )}

      {hasFilter ? (
        <>
          {loading && (
            <div className="d-flex flex-column gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="skeleton" style={{ width: "48px", height: "48px", borderRadius: "12px" }} />
                    <div className="skeleton" style={{ height: "14px", width: "40%", borderRadius: "4px" }} />
                  </div>
                  <div className="skeleton" style={{ height: "180px", borderRadius: "16px" }} />
                </div>
              ))}
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="d-flex flex-column gap-4">
              {results.map((g) => (
                <RestaurantFoodGroup key={g.restaurant.id} group={g} />
              ))}
            </div>
          )}

          {!loading && results.length === 0 && (
            <div className="text-center text-muted py-5">
              <i className="bi bi-search d-block mb-2" style={{ fontSize: "1.8rem" }}></i>
              {failed ? t("common.error") : searchTerm ? t("search.noResults") : t("categories.noResults")}
            </div>
          )}
        </>
      ) : (
        <AllRestaurants />
      )}
    </div>
  );
}

export default TumMekanlar;
